import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import type { ReactNode } from 'react';
import { cx } from '../../lib/cx.js';
import { formatPercentDelta } from '../../lib/format.js';

export interface StatTileProps {
  label: string;
  value: ReactNode;
  /** Signed percentage change against the named period. */
  delta?: number | null;
  deltaPeriod?: string;
  /** Whether a rise is good news (alarm counts: no; uptime: yes). */
  upIsGood?: boolean;
  /** 12-ish point sparkline drawn in the de-emphasis hue. */
  trend?: number[];
  tone?: string;
  footnote?: ReactNode;
  className?: string;
  onClick?: () => void;
}

/**
 * Stat tile: label (sentence case) / value / optional delta / optional
 * sparkline. Proportional figures on the big number, tabular elsewhere.
 */
export function StatTile({
  label,
  value,
  delta,
  deltaPeriod = 'previous period',
  upIsGood = false,
  trend,
  tone,
  footnote,
  className,
  onClick,
}: StatTileProps) {
  const Wrapper = onClick ? 'button' : 'div';
  return (
    <Wrapper
      {...(onClick ? { type: 'button' as const, onClick } : {})}
      className={cx(
        'flex flex-col gap-1 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)] p-3 text-left',
        'shadow-[var(--shadow-1)] transition-colors duration-[var(--dur-fast)]',
        onClick && 'hover:border-[var(--border-strong)]',
        className,
      )}
    >
      <span className="text-[var(--text-sm)] text-[var(--text-2)]">{label}</span>
      <span
        className="text-[var(--text-2xl)] font-semibold leading-tight tracking-[-0.02em]"
        style={tone ? { color: tone } : undefined}
      >
        {value}
      </span>
      <div className="flex items-center gap-2">
        {delta !== undefined && (
          <DeltaPill delta={delta} period={deltaPeriod} upIsGood={upIsGood} />
        )}
        {trend && trend.length > 1 && <Sparkline values={trend} />}
      </div>
      {footnote && <span className="text-[var(--text-sm)] text-[var(--text-3)]">{footnote}</span>}
    </Wrapper>
  );
}

export function DeltaPill({
  delta,
  period,
  upIsGood = false,
}: {
  delta: number | null;
  period: string;
  upIsGood?: boolean;
}) {
  if (delta === null) {
    return <span className="text-[var(--text-sm)] text-[var(--text-3)]">no prior data</span>;
  }
  const flat = Math.abs(delta) < 0.5;
  const good = flat ? null : delta > 0 === upIsGood;
  const Icon = flat ? Minus : delta > 0 ? ArrowUpRight : ArrowDownRight;
  const colour = flat ? 'var(--text-3)' : good ? 'var(--success)' : 'var(--danger)';
  return (
    <span
      className="tnum inline-flex items-center gap-0.5 text-[var(--text-sm)] font-medium"
      style={{ color: colour }}
      title={`vs ${period}`}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {formatPercentDelta(flat ? 0 : delta)}
    </span>
  );
}

/** 2px sparkline; no axes, no labels - it is texture, not a chart. */
export function Sparkline({
  values,
  width = 64,
  height = 18,
  color = 'var(--chart-1)',
}: {
  values: number[];
  width?: number;
  height?: number;
  color?: string;
}) {
  if (values.length < 2) return null;
  const max = Math.max(1, ...values);
  const step = width / (values.length - 1);
  const path = values
    .map((v, i) => `${i === 0 ? 'M' : 'L'} ${i * step} ${height - (Math.max(0, v) / max) * height}`)
    .join(' ');
  return (
    <svg width={width} height={height} aria-hidden className="shrink-0">
      <path d={path} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" />
    </svg>
  );
}
